import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './DatePicker.less';
import dayjs from 'dayjs';
import Icon from '../Icon/Icon';
import Calendar from '../Calendar/Calendar';
import DatePickerOverlay from './DatePickerOverlay';
import { APP_TIMEZONE, dayjsTZ } from '../../../helper/DateHelper';

interface Props {
    value: Date;
    minDate: Date;
    maxDate: Date;
    includedDates?: Array<Date>;
    onChange: (date: Date) => void;
    readonly?: boolean;
}

// ── Berlin ↔ local-day bridge ────────────────────────────────────────────────
// A reading date is a Berlin calendar day. react-day-picker renders/compares days in
// LOCAL time, so at this single boundary we convert each Berlin day → a local-midnight
// Date for the calendar, and convert the clicked local day back → a Berlin-midnight Date
// for onChange. This keeps the picker correct regardless of the device timezone.
//
//   value/min/max (Berlin) ──toLocalMidnight──▶ Calendar (local)
//   Calendar onSelect (local day) ──localDayString──▶ berlinMidnight ──▶ onChange (Berlin)
const pad = (n: number) => String(n).padStart(2, '0');
const berlinDayString = (d: Date): string => dayjsTZ(d).format('YYYY-MM-DD');
const localDayString = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Parse the day in Berlin, not as device-local midnight (dayjsTZ('YYYY-MM-DD') would do the latter).
const berlinMidnight = (day: string): Date => dayjs.tz(day, APP_TIMEZONE).toDate();
const nextDayString = (day: string): string => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};
const toLocalMidnight = (berlinDate: Date): Date => {
    const [y, m, d] = berlinDayString(berlinDate).split('-').map(Number);
    return new Date(y, m - 1, d);
};

const DatePicker = ({ value, minDate, maxDate, onChange, readonly = false, includedDates }: Props) => {
    const { t } = useTranslation();
    // Allow-list = (min..max) ∪ includedDates, deduped, as Berlin 'YYYY-MM-DD' strings.
    const allowList = useMemo(() => buildIncludedDates(minDate, maxDate, includedDates), [minDate, maxDate, includedDates]);
    const allow = useMemo(() => new Set(allowList.map(berlinDayString)), [allowList]);

    const firstAllowed = allowList[0];
    const lastAllowed = allowList[allowList.length - 1];

    // Read-only when the caller fixes it OR only a single day is selectable → no overlay.
    const isReadOnly = readonly || allowList.length <= 1;

    const trigger = (
        <div className={'datepicker-field'}>
            <span className={'datepicker-field__label'}>{dayjsTZ(value).format('DD.MM.YYYY')}</span>
            <Icon aria={{ 'aria-hidden': true }} path={Icon.Path.mdiCalendar} className={'datepicker-field__icon'} />
        </div>
    );

    // Open on the selected month when the value is valid, otherwise on the first allowed month.
    const initialBerlinMonth = allow.has(berlinDayString(value)) ? value : firstAllowed;

    return (
        <DatePickerOverlay
            trigger={trigger}
            readOnly={isReadOnly}
            title={t('common.selectReadingDate')}
            ariaLabel={`${t('common.selectReadingDate')}: ${dayjsTZ(value).format('DD.MM.YYYY')}`}
        >
            {(close, isMobile) => (
                <Calendar
                    selected={toLocalMidnight(value)}
                    defaultMonth={toLocalMidnight(initialBerlinMonth)}
                    fromMonth={toLocalMidnight(firstAllowed)}
                    toMonth={toLocalMidnight(lastAllowed)}
                    disabled={(day: Date) => !allow.has(localDayString(day))}
                    fullWidth={isMobile}
                    onSelect={(localDay) => {
                        onChange(berlinMidnight(localDayString(localDay)));
                        close();
                    }}
                />
            )}
        </DatePickerOverlay>
    );
};

/**
 * Builds the set of selectable days: every day in [minDate, maxDate] PLUS any `extraDates`
 * (which may fall outside that range — `includedDates` *extends* the selection, it does not
 * clip it). Works on Berlin 'YYYY-MM-DD' strings only: add()/startOf('day') on dayjs tz objects
 * shift a day across DST changes (and depend on the device's own DST rules).
 */
export const buildIncludedDates = (minDate: Date, maxDate: Date, extraDates?: Date[]) => {
    const days = new Set<string>();

    // An invalid bound formats as 'Invalid Date', which would never end the string loop.
    if (dayjs(minDate).isValid() && dayjs(maxDate).isValid()) {
        const last = berlinDayString(maxDate);

        for (let day = berlinDayString(minDate); day <= last; day = nextDayString(day)) {
            days.add(day);
        }
    }

    extraDates?.forEach((e) => days.add(berlinDayString(e)));

    return Array.from(days).sort().map(berlinMidnight);
};

export default DatePicker;
